import {
  EntityImportTableForm,
  NAMESPACE,
} from '@/modules/import/EntityImportTable.types';

/** Applies `update` to every row of the import form, keeping its shape. */
function mapEntityRows(
  formData: EntityImportTableForm,
  update: (
    entity: EntityImportTableForm[typeof NAMESPACE]['householdMap'][string]['entityMap'][string]
  ) => typeof entity
): EntityImportTableForm {
  const householdMap = Object.fromEntries(
    Object.entries(formData[NAMESPACE].householdMap).map(
      ([householdId, household]) => [
        householdId,
        {
          ...household,
          entityMap: Object.fromEntries(
            Object.entries(household.entityMap).map(([id, entity]) => [
              id,
              update(entity),
            ])
          ),
        },
      ]
    )
  );

  return { ...formData, [NAMESPACE]: { ...formData[NAMESPACE], householdMap } };
}

/**
 * Rows ticked for import that have no entity type chosen.
 *
 * `mapFormDataToMutation` silently drops these, so without this check a Done
 * that selected only untyped rows would produce no inputs, report success and
 * import nothing. AI inference usually fills the type in, but it can be off or
 * fail to infer one.
 */
export function hasUntypedSelectedEntities(
  formData: EntityImportTableForm
): boolean {
  return Object.values(formData[NAMESPACE].householdMap).some((household) =>
    Object.values(household.entityMap).some(
      (entity) => entity.shouldImport && !entity.entityKind
    )
  );
}

/**
 * Unticks every row, for use as the post-import baseline. What was just
 * imported must not be sent again if a later pane fails and the user retries.
 */
export function withImportSelectionsCleared(
  formData: EntityImportTableForm
): EntityImportTableForm {
  return mapEntityRows(formData, (entity) => ({
    ...entity,
    shouldImport: false,
  }));
}

/**
 * Returns the import form with any row whose integration entity has been
 * claimed by a link pane removed.
 *
 * The import table lists integration entities that had no Luminary entity
 * attached when its query ran. Within a single "Manage integration data"
 * session the "Link data" step can attach one after that point without this
 * table requerying, so a row can still look importable when it is about to be
 * linked. Committing both would attach the integration entity to the existing
 * entity and create a duplicate from it.
 */
export function withoutClaimedEntities(
  formData: EntityImportTableForm,
  claimedIntegrationEntityIds: Set<string>
): EntityImportTableForm {
  if (claimedIntegrationEntityIds.size === 0) return formData;

  const householdMap = Object.fromEntries(
    Object.entries(formData[NAMESPACE].householdMap).map(
      ([householdId, household]) => [
        householdId,
        {
          ...household,
          entityMap: Object.fromEntries(
            Object.entries(household.entityMap).filter(
              ([, entity]) =>
                !claimedIntegrationEntityIds.has(entity.integrationEntityId)
            )
          ),
        },
      ]
    )
  );

  return { ...formData, [NAMESPACE]: { ...formData[NAMESPACE], householdMap } };
}
